import {
  INCOMING_INSPECTION_KEY,
  mutateIncomingInspection,
} from "@/api/incomingInspection/useIncomingInspection";
import SubBagInputGrid from "@/features/inventory/components/form/inspection/SubBagInputGrid";
import SubInspectionTable from "@/features/inventory/components/form/inspection/SubInspectionTable";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { Calendar, isCalendarDate } from "@/features/ui/calendar";
import { setDateFormatString } from "@/utils/dateTimeUtil";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Flex, NumberInput, Table, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IncomingInspectionsGet200ResponseRowsInner,
  IncomingInspectionsGetRequest,
  SpcApiIncomingInspectionsIncomingInspectionIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendarEvent } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FormatterProps } from "react-data-grid";

interface IncomingInspectionUpdateFormProps {
  formatterProps: FormatterProps<IncomingInspectionsGet200ResponseRowsInner>;
}

const IncomingInspectionUpdate = Object.assign({}, FormMain, {
  Wrapper: FormWrapper,
  Table: SubInspectionTable,
  Input: TextInput,
  NumberInput: NumberInput,
  Button: Button,
  SubBagInputGrid: SubBagInputGrid,
});

const BAG_NUM = 40;

export const IncomingInspectionUpdateForm = ({
  formatterProps,
}: IncomingInspectionUpdateFormProps) => {
  const { row } = formatterProps;

  const queryClient = useQueryClient();
  const incomingDate = row.createdAt ? new Date(row.createdAt as string) : null;
  const [startDate, setStartDate] = useState<Date | null>(incomingDate);
  const [endDate, setEndDate] = useState<Date | null>(incomingDate);
  const { openModal, closeModal } = useModal();

  const form = useForm<IncomingInspectionsGetRequest>({
    initialValues: {
      ...row,
      partnerCode: row.partnerCode as string,
      itemCode: row.itemCode as string,
    },
    validate: {
      lotId: (value) => (value ? null : "로트번호를 입력해주세요."),
      partnerCode: (value) => (value ? null : "거래처를 선택해주세요."),
    },
  });

  const defectBagCount = form.values.details?.filter((bag) => bag.isReturned).length;

  const goodQuantity = form.values.details?.length
    ? form.values.details?.reduce(
        (sum, bag) =>
          !bag.isReturned ? Number(sum ?? 0) + Number(bag.quantity ?? 0) : Number(sum ?? 0),
        0,
      )
    : 0;
  const defectQuantity = form.values.details?.reduce(
    (sum, bag) =>
      bag.isReturned ? Number(sum ?? 0) + Number(bag.quantity ?? 0) : Number(sum ?? 0),
    0,
  );

  const goodBagCount = form.values.details?.filter((bag) => !bag.isReturned && bag.quantity).length;

  const { mutate: updateMutate } = useMutation(
    (params: SpcApiIncomingInspectionsIncomingInspectionIdPutRequest) =>
      mutateIncomingInspection.update(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([INCOMING_INSPECTION_KEY]);
        closeModal(formatterProps);
        customAlert("입고/수입검사 수정에 성공하였습니다.", "수정 성공", "green");
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "입고/수입검사" }),
          "입고/수입검사 수정 실패",
          "red",
        );
      },
    },
  );

  const handleSubmit = async () => {
    const inspectionParams: SpcApiIncomingInspectionsIncomingInspectionIdPutRequest = {
      incomingInspectionId: row.id as number,
      incomingInspectionsGetRequest: {
        ...form.values,
        goodQuantity: String(goodQuantity ?? 0),
        goodBagCount: String(goodBagCount ?? 0),
        defectQuantity: String(defectQuantity ?? 0),
        defectBagCount: String(defectBagCount ?? 0),
        details: form?.values?.details?.map((bag, index) => ({
          seq: index,
          quantity: bag.quantity,
          isReturned: bag.isReturned,
        })),
      },
    };

    updateMutate(inspectionParams);
  };

  const openIncomingDateCalendar = () => {
    openModal(
      <Calendar type="default" dates={[startDate, endDate]} />,
      null,
      "날짜 선택",
      true,
    ).then((value) => {
      if (isCalendarDate(value)) {
        setStartDate(value[0]);
        setEndDate(value[1]);
        form.setFieldValue("inspectionAt", value[0]?.toISOString());
      }
    });
  };

  return (
    <IncomingInspectionUpdate.Wrapper onSubmit={form.onSubmit(handleSubmit)} miw={1000} maw={1600}>
      <Title underline align="center" mb={"xl"}>
        {"입고 / 수입 검사 내역서"}
      </Title>
      <Flex fw={"bold"} mb={"xl"} align={"center"} justify={"space-between"}>
        <Flex align={"center"}>
          <Text pr={"xs"} w={"5rem"}>
            거래처 :{" "}
          </Text>
          <PartnerAutoComplete
            placeholder="거래처 선택"
            {...form.getInputProps(`partnerCode`)}
            setName={(name: string) => form.setFieldValue("partnerName", name)}
          />
        </Flex>
        <Flex gap={"xl"}>
          <Text
            role="button"
            tabIndex={0}
            title="입고일자 수정"
            onClick={openIncomingDateCalendar}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openIncomingDateCalendar();
              }
            }}
            style={{
              alignItems: "center",
              color: "#1c7ed6",
              cursor: "pointer",
              display: "inline-flex",
              gap: 4,
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            <IconCalendarEvent size={16} />
            입고 일자 : {setDateFormatString(startDate ?? undefined)}
          </Text>
          <Text>로트No. : {row.lotData?.name ?? row.lotData?.expiration ?? "-"}</Text>
        </Flex>
      </Flex>
      <Table withBorder withColumnBorders highlightOnHover>
        <thead>
          <tr>
            <th style={{ textAlign: "center", fontWeight: "bold" }}>{"품명"}</th>
            <th style={{ textAlign: "center", fontWeight: "bold" }}>{"Grade"}</th>
            <th style={{ textAlign: "center", fontWeight: "bold" }}>{"Color"}</th>
            <th style={{ textAlign: "center", fontWeight: "bold" }}>{"형태"}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ textAlign: "center" }}>{row.itemWithGrade?.name ?? "-"}</td>
            <td style={{ textAlign: "center" }}>{row.itemWithGrade?.gradeName ?? "-"}</td>
            <td style={{ textAlign: "center" }}>{row.itemWithGrade?.itemCategoryName ?? "-"}</td>
            <td style={{ textAlign: "center" }}>{row.itemWithGrade?.abstractItemCode ?? "-"}</td>
          </tr>
        </tbody>
      </Table>
      <IncomingInspectionUpdate.Table
        title="수입검사"
        columns={["RoHS 확인", "재질(연소)", "포장상태", "수분상태"]}
        form={form}
      />
      <Table withBorder withColumnBorders highlightOnHover>
        <tbody>
          <tr>
            <td>
              <Text align="center" fw={"bold"}>
                {"양품수량"}
              </Text>
            </td>
            <td>
              <Text align="center" fw={"bold"}>
                <IncomingInspectionUpdate.Input value={goodQuantity ?? 0} />
              </Text>
            </td>
            <td>
              <Text align="center" fw={"bold"}>
                {"반품수량"}
              </Text>
            </td>
            <td>
              <Text align="center" fw={"bold"}>
                <IncomingInspectionUpdate.Input value={defectQuantity ?? 0} />
              </Text>
            </td>
          </tr>
          <tr>
            <td>
              <Text align="center" fw={"bold"}>
                {"양품 Bag수"}
              </Text>
            </td>
            <td>
              <Text align="center" fw={"bold"}>
                <IncomingInspectionUpdate.Input value={goodBagCount ?? 0} />
              </Text>
            </td>
            <td>
              <Text align="center" fw={"bold"}>
                {"반품 Bag수"}
              </Text>
            </td>
            <td>
              <Text align="center" fw={"bold"}>
                <IncomingInspectionUpdate.Input value={defectBagCount ?? 0} />
              </Text>
            </td>
          </tr>
          <tr>
            <td>
              <Text align="center" fw={"bold"}>
                {"반품 사유"}
              </Text>
            </td>
            <td colSpan={3}>
              <IncomingInspectionUpdate.Input
                placeholder="반품 사유를 입력해주세요"
                {...form.getInputProps("defectReason")}
              />
            </td>
          </tr>
        </tbody>
      </Table>
      <IncomingInspectionUpdate.SubBagInputGrid bagNum={BAG_NUM} form={form} />
      <Flex gap={"xs"} justify={"flex-end"}>
        <IncomingInspectionUpdate.Button color="gray" onClick={() => closeModal({})}>
          {"취소"}
        </IncomingInspectionUpdate.Button>
        <IncomingInspectionUpdate.Button type="submit">{"수정"}</IncomingInspectionUpdate.Button>
      </Flex>
    </IncomingInspectionUpdate.Wrapper>
  );
};
